import { useCallback, useMemo } from 'react';

import { Link } from '@tanstack/react-router';
import { ExternalLinkIcon } from 'lucide-react';
import type { UseFormReturn } from 'react-hook-form';

import { Avatar, AvatarFallback, AvatarImage, Button, Icon, Separator, Text } from '@/_components';
import { CardContainer, ContainerLayout } from '@/_containers';
import { dateTime, nameResolver } from '@/_shared';

import { Conversation, stripInlineStylesColor, SubHeaderSection, ticketTypeMapper } from './components/index.ts';
import { usePage } from './use-page.ts';

import type { SupportTicketDetailSchema } from '../shared/schemas/support-query-schema';

export default function Page({ ticketDetailsForm }: { ticketDetailsForm: UseFormReturn<SupportTicketDetailSchema> }) {
  const { ticket } = usePage({ ticketDetailsForm });

  const handleOpenInFreshdesk = useCallback(() => {
    window.open(`https://pdx.freshdesk.com/a/tickets/${ticket.id}`, '_blank');
  }, [ticket]);

  const { description, conversations, sourceInfo, agent, requester, source, cc_emails, isOutBound, to_emails } =
    useMemo(() => {
      return {
        // !important we have removed styles to show properly in our UI
        // if used with Text Editor then We should provide actual description.
        description: stripInlineStylesColor(ticket.description),
        sourceInfo: ticketTypeMapper(ticket.source),
        conversations: ticket.conversations.map((c) => ({
          ...c,
          body: stripInlineStylesColor(c.body),
        })),
        agent: ticket.agent,
        source: ticket.source,
        requester: ticket.requester,
        isOutBound: ticket.source !== 10,
        to_emails: ticket.to_emails ?? [],
        cc_emails: ticket.cc_emails ?? [],
      };
    }, [ticket]);

  if (!ticket) {
    return (
      <ContainerLayout>
        <div className="py-8 text-center">
          <p className="text-c-card-muted">Ticket not found.</p>
        </div>
      </ContainerLayout>
    );
  }

  const { id: ticketAgent, first_name, last_name, profile_image } = (isOutBound ? requester : agent) || {};
  const link = isOutBound ? '/contacts/$id' : '/team-members/$id';
  const name = nameResolver(first_name, last_name, '***UNKNOWN***');

  return (
    <>
      <CardContainer className="space-y-6" bodyClassName="3xl:px-6 px-4">
        <header className="mb-6 flex flex-row items-center justify-between gap-2 px-4">
          <div className="flex items-center gap-4">
            <div className="flex size-8 items-center justify-center">
              <Icon icon={sourceInfo.icon} className="text-accent dark:text-white" />
            </div>
            <h3 className="text-accent text-xl font-semibold dark:text-white">{ticket.subject}</h3>
          </div>
          <Button
            as="link"
            iconLeft={ExternalLinkIcon}
            className="dark:text-primary -mr-4"
            size="sm"
            onClick={handleOpenInFreshdesk}
          >
            Open in Freshdesk
          </Button>
        </header>

        <main className="flex flex-col gap-6 md:flex-row">
          <article className="flex flex-col gap-4">
            <section className="px-4">
              <header className="mb-6 flex items-center gap-3">
                <Avatar>
                  <AvatarImage src={profile_image?.[0]?.url || undefined} />
                  <AvatarFallback name={name} className="text-sm" />
                </Avatar>
                <div>
                  <Text as="p" className="text-c-card-muted font-medium">
                    {ticketAgent ? (
                      <Link
                        to={link}
                        className="decoration-primary text-primary hover:underline"
                        params={{ id: ticketAgent?.toString() || '' }}
                      >
                        {name}
                      </Link>
                    ) : (
                      <span className="text-primary">{name}</span>
                    )}
                    <span className="lowercase">
                      {` ${!isOutBound ? 'emailed ' + (ticket?.requester?.emails?.[0]?.email ?? '') : 'requested via ' + sourceInfo.label}`}
                    </span>
                  </Text>
                  <Text as="p" className="text-xs text-gray-400 italic">
                    {dateTime.fromNow(ticket.created_at)} (
                    {dateTime.format(ticket.created_at, 'ddd, MMMM DD, YYYY [at] hh:mm A')})
                  </Text>
                </div>
              </header>

              <SubHeaderSection
                cc_emails={cc_emails ?? []}
                source={source}
                to_emails={to_emails ?? []}
                sentLabel={isOutBound ? 'To' : 'From'}
              />

              <section className="flex items-center gap-3">
                <div className="size-8" />
                <div
                  className="text-accent __fresh-desk-body block w-full text-sm dark:text-white"
                  dangerouslySetInnerHTML={{ __html: description }}
                />
              </section>
            </section>

            <Separator />

            <section className="flex flex-col gap-y-2">
              <Conversation conversations={conversations} />
            </section>
          </article>
        </main>
      </CardContainer>
    </>
  );
}
